import { CSSProperties, FC, ReactNode, useContext } from "react";
import { twMerge } from "tailwind-merge";

import { HoverableToolbarContext } from "components/hover-widgets/HoverableToolbarProvider";
import { DownloadUploadOptions } from "components/menus/DownloadUploadOptions";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button, type ButtonProps } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import {
  Menu,
  type MenuChangeEvent,
  MenuDirection,
  MenuDivider,
  MenuInstance,
  MenuItem,
} from "mds/components/Menu";
import { ClockIcon, DotMenuVerticalIcon, DuplicateCopyIcon, TrashDeleteIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectIsTimeline } from "store/selectors/view";
import { viewStateActions } from "store/slices/view";

// This is the base component for SideBarOptionsMenu.  It is used in the other components
// that extend it for different models ex: Course, Topic, Activity, Resource.
export type SidebarOptionsMenuProps = {
  styles?: CSSProperties;
  size?: ButtonProps<"button">["size"];
  className?: string;
  direction?: MenuDirection;
  children?: ReactNode;
  instanceRef?: React.Ref<MenuInstance>;
  eventPrefix?: string;
  limitItemHeight?: boolean;
  optionsMenuActions?: {
    onDelete?: (() => void) | (() => Promise<void>);
    isDeleteDisabled?: boolean;
    isDuplicateDisabled?: boolean;
    deleteTooltipText?: string;
    onDuplicate?: () => void;
    showDownloadUploadOptions?: boolean;
    showTimeline?: boolean;
    page?: PageType;
  };
  kind?: "squareLegacy" | "gray";
};

export const SidebarOptionsMenu: FC<SidebarOptionsMenuProps> = ({
  styles,
  size = "s",
  className,
  direction = "right",
  children = null,
  optionsMenuActions,
  instanceRef = null,
  eventPrefix,
  limitItemHeight,
  kind = "squareLegacy",
}) => {
  // TODO: This should be tertiary, but it needs to handle the `active` styling with blue
  // background that comes from the parent `ListItem`.
  const MenuButton = (
    <Button
      className={twMerge(
        kind === "squareLegacy"
          ? "border-0 bg-[inherit] p-2 text-black-tint-20 hover:bg-black-tint-93 hover:text-black-tint-20 group-data-[selected]/listitem:text-white group-data-[unreleased]/listitem:text-black-tint-55 hover:group-data-[selected]/listitem:text-black-tint-20"
          : "",
        className,
      )}
      eventName={eventPrefix}
      kind={kind}
      size={size}
      title={t("common.options")}
      isSquare
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <DotMenuVerticalIcon />
    </Button>
  );

  const hoverableContext = useContext(HoverableToolbarContext);

  const onMenuChange = (event: MenuChangeEvent) => {
    hoverableContext?.setShouldStayOpen?.(event.open);
  };

  return (
    <Menu
      direction={direction}
      instanceRef={instanceRef}
      limitItemHeight={limitItemHeight}
      menuButton={MenuButton}
      menuClassName="min-w-[180px]"
      menuStyle={styles}
      onMenuChange={onMenuChange}
    >
      {children}
      {optionsMenuActions?.onDuplicate && (
        <MenuItem
          disabled={optionsMenuActions?.isDuplicateDisabled}
          eventName={eventPrefix ? `${eventPrefix}Duplicate` : undefined}
          onClick={optionsMenuActions.onDuplicate}
        >
          <IconText iconStart={<DuplicateCopyIcon />} text={t("sidebar_options_menu.duplicate")} />
        </MenuItem>
      )}
      {(optionsMenuActions?.showDownloadUploadOptions || optionsMenuActions?.showTimeline) &&
        optionsMenuActions.page && (
          <>
            <MenuDivider />
            {optionsMenuActions?.showDownloadUploadOptions && (
              <DownloadUploadOptions page={optionsMenuActions.page} />
            )}
            {optionsMenuActions?.showTimeline && <TimelineToggleButton />}
          </>
        )}
      {optionsMenuActions?.onDelete && (
        <>
          {Object.keys(optionsMenuActions).filter((key) => !key.toLowerCase().includes("delete"))
            .length > 1 && <MenuDivider />}
          {/* TODO: The disabled state for the Delete action should be greyed out instead of hoverable & red. */}
          <MenuItem
            disabled={optionsMenuActions.isDeleteDisabled}
            eventName={eventPrefix ? `${eventPrefix}Delete` : undefined}
            kind="destructive"
            title={optionsMenuActions.deleteTooltipText}
            onClick={optionsMenuActions.onDelete}
          >
            <IconText
              iconStart={
                <TrashDeleteIcon
                  className={
                    optionsMenuActions.isDeleteDisabled ? "text-black-tint-55" : "text-red"
                  }
                />
              }
              text={t("common.delete")}
            />
          </MenuItem>
        </>
      )}
    </Menu>
  );
};

export const TimelineToggleButton: FC = () => {
  const dispatch = useAppDispatch();
  const isTimelineView = useAppSelector(selectIsTimeline);
  const text = isTimelineView
    ? t("worksheet.timeline.hide_menu_label")
    : t("worksheet.timeline.show_menu_label");
  return (
    <MenuItem
      title={text}
      onClick={() => dispatch(viewStateActions.setIsTimelineView(!isTimelineView))}
    >
      <IconText iconStart={<ClockIcon />} text={text} />
    </MenuItem>
  );
};
